import { useState, useEffect, useRef } from "react";
import { DrugSearchResult, searchDrugs } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

interface UseDrugSearchResult {
  drugs: DrugSearchResult[];
  isLoading: boolean;
}

export function useDrugSearch(query: string): UseDrugSearchResult {
  const { currentUser } = useAuth();
  const [drugs, setDrugs] = useState<DrugSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query || query.trim().length < 2) {
      setDrugs([]);
      setIsLoading(false);
      return;
    }

    const doctorId = currentUser?.doctor_id || currentUser?.id || "";
    if (!doctorId) {
      setDrugs([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await searchDrugs(query.trim(), doctorId);
        setDrugs(results);
      } catch {
        setDrugs([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, currentUser?.doctor_id, currentUser?.id]);

  return { drugs, isLoading };
}
